import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Screen-reader helpers shared by the lesson workspace layouts.
 *
 * Each layout has one visually hidden status line. It speaks short summaries
 * ("Claude replied", "Run finished. Tests passed: 3 of 4") so a whole reply or
 * console output is never read out from there; the chat log reads replies.
 */
export const LiveStatus = ({ message }) => (
    <div role="status" className="sr-only">
        {message}
    </div>
);

// [message, announce]. The same text twice would not change the region, so it
// would not be read again; a trailing no-break space makes it a change.
export const useStatus = () => {
    const [message, setMessage] = useState("");
    const announce = useCallback(
        (text) => setMessage((current) => (current === text ? `${text} ` : text)),
        []
    );
    return [message, announce];
};

// Calls onChange(value) whenever `value` changes after mount. Never on mount, so
// a lesson that opens already finished stays quiet.
export const useOnChange = (value, onChange) => {
    const previous = useRef(value);
    const callback = useRef(onChange);
    callback.current = onChange;
    useEffect(() => {
        if (previous.current === value) return;
        previous.current = value;
        callback.current(value);
    }, [value]);
};

// Focuses `el` only if focus fell to <body>, which is where it goes when the
// focused control is disabled or removed. Returns whether it moved focus.
export const refocusIfLost = (el) => {
    const active = document.activeElement;
    if (!el || el.disabled || (active && active !== document.body)) return false;
    el.focus();
    return true;
};

export const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
